import { Grid, makeStyles, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import clsx from "clsx";
import { PageLoading } from "components/UI/Page";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { lang } from "../../i18nLang";
import {
  ContractIntegrationStore as Integration,
  SendToBillingStore as Store,
} from "../../store";
import { formatNumberToMonetary } from "../../utils";
import { DialogModalWithSteps } from "../UI/DialogModals";
import TabPanel from "../UI/Tabs/TabPanel";
import FormIntegrationToBilling from "./FormIntegrationToBilling";
import MeasurementDetails from "./MeasurementDetails";
import ProductItemsSummary from "./ProductItemsSummary";
import ProductItemsTable from "./ProductItemsTable";
import ProdutcItemsToolbar from "./ProdutcItemsToolbar";
import ResourcesByCity from "./ResourcesByCity";
import ResourcesByCitySummary from "./ResourcesByCitySummary";
import ResumeItems from "./ResumeItems";

const useStyles = makeStyles({
  dialogFull: {
    "& .MuiDialog-paper": {
      width: "100%",
      maxWidth: "100%",
      height: "calc(100% - 128px)",
      margin: 64,
    },
  },
  blockScroll: {
    "& .MuiDialogContent-root": {
      overflow: "hidden",
    },
  },
  fullHeight: {
    height: "100%",
  },
});

interface IProps {
  /** Propriedade que indica se o modal está aberto ou fechado. */
  open: boolean;
  /** Função que será chamada ao fechar o modal. */
  onClose: Function;
  /** ID da medição que será faturada, caso não seja passado nenhum valor o sistema irá pegar a medição atual. */
  measurementId?: string;
}

/** Janela modal para preenchimento dos dados a serem enviados para Faturamento
 * com a medição.
 * @category Components
 */
const SendToBilling = observer(({ open, onClose, measurementId }: IProps) => {
  const classes = useStyles();
  const measurement = Store.selectedMeasurement;
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      title: lang("billing.steps.client"),
      valid: Store.integrationDataAreValid(),
    },
    { title: lang("billing.steps.measurement"), valid: true },
    {
      title: lang("billing.steps.itemSelection"),
      valid: Store.productsItemsDataAreValid(),
    },
    {
      title: lang("billing.steps.resourcesByCounty"),
      valid: Store.citiesItemsDataAreValid(),
    },
    { title: lang("billing.steps.resume"), valid: true },
  ];

  useEffect(() => {
    if (open) {
      setLoading(true);
      Store.getDataToSendBilling(measurementId).finally(() =>
        setLoading(false)
      );
    }
  }, [open, measurementId]);

  const handleClickClose = () => {
    Store.reset();
    setActiveStep(0);
    onClose();
  };

  const handleNext = (currentStep: number, nextStep: number) => {
    setActiveStep(nextStep);
  };

  const handleClickFinish = () => {
    if (
      Store.integrationDataAreValid() &&
      Store.productsItemsDataAreValid() &&
      Store.citiesItemsDataAreValid()
    ) {
      Store.sendAndSaveDataToBilling();
    }
  };

  const canSend = Store.canSendMeasurement();
  const disabledButton = !Integration.isAuthenticated || !canSend;

  return (
    <DialogModalWithSteps
      title={lang("billing.title")}
      open={open}
      activeStep={activeStep}
      className={clsx(classes.dialogFull, {
        [classes.blockScroll]: activeStep === 2 || activeStep === 4,
      })}
      maxWidth={"md"}
      steps={steps}
      onClose={handleClickClose}
      onFinish={handleClickFinish}
      onNext={handleNext}
      disabledFinishButton={disabledButton}
    >
      {!canSend && (
        <Alert severity="info" style={{ margin: "1rem 0" }}>
          <Typography>{lang("billing.alreadyBilled")}</Typography>
        </Alert>
      )}
      {canSend && (
        <>
          <TabPanel value={activeStep} index={0} justifyContent="center">
            {!Integration.isAuthenticated && (
              <Alert severity="error" style={{ margin: "1rem 0" }}>
                <Typography>{lang("billing.noAuth")}</Typography>
              </Alert>
            )}
            {loading && <PageLoading style={{ padding: "3rem 0" }} />}
            {!loading && <FormIntegrationToBilling />}
          </TabPanel>
          <TabPanel value={activeStep} index={1} justifyContent="center">
            <Grid container>
              <MeasurementDetails
                title={lang("billing.fields.totalMeasured")}
                value={formatNumberToMonetary(
                  measurement?.getReadjustedTotal() || 0
                )}
              />
              <MeasurementDetails
                title={lang("billing.fields.billing")}
                value={formatNumberToMonetary(
                  measurement?.getBillingsTotal() || 0
                )}
              />
              <MeasurementDetails
                title={lang("billing.fields.totalReceivable")}
                value={formatNumberToMonetary(measurement?.getNetTotal() || 0)}
              />
            </Grid>
          </TabPanel>
          <TabPanel value={activeStep} index={2} justifyContent="center">
            <ProdutcItemsToolbar />
            <ProductItemsTable />
            <ProductItemsSummary />
          </TabPanel>
          <TabPanel
            className={classes.fullHeight}
            value={activeStep}
            index={3}
            justifyContent="center"
          >
            <ResourcesByCity />
            <ResourcesByCitySummary />
          </TabPanel>
          <TabPanel
            className={classes.fullHeight}
            value={activeStep}
            index={4}
            justifyContent="flex-start"
          >
            <ResumeItems
              rowsData={Store.itemsToSend}
              onCreate={() => Store.getItemsReadyToSend()}
            />
          </TabPanel>
        </>
      )}
    </DialogModalWithSteps>
  );
});

export default SendToBilling;
